import { ClientOptions, ClientContext, ORPCError } from "@orpc/client";
import { defaultRequestTimeoutMs, getTimeout } from "./timeout";

const responseHeaderKeys = ["X-Correlation", "X-Kepler-Build-Version", "X-SAS-Build-Version"];

const isParsableJson = (body: string) => {
    try {
        JSON.parse(body);
        return true;
    } catch {
        return false;
    }
};

const linkAbortSignal = (controller: AbortController, signal: AbortSignal) => {
    const abortRequest = () => controller.abort(signal.reason);
    if (signal.aborted) {
        abortRequest();
    } else {
        signal.addEventListener("abort", abortRequest, { once: true });
    }
    return () => signal.removeEventListener("abort", abortRequest);
};

const createRequestController = (signal: AbortSignal, timeout: number) => {
    const controller = new AbortController();
    // isDefaultTimeout tells the query retry policy (Core/Query/queryRetryPolicy.ts) whether a timed-out call may be retried.
    const abortOnTimeout = () =>
        controller.abort(
            new ORPCError("AbortError", {
                message: `Request timed out at ${timeout} ms`,
                data: { timeoutMs: timeout, isDefaultTimeout: timeout === defaultRequestTimeoutMs }
            })
        );
    const timer = setTimeout(abortOnTimeout, timeout);
    const unlinkAbortSignal = linkAbortSignal(controller, signal);
    let disposed = false;
    const dispose = () => {
        if (disposed) {
            return;
        }
        disposed = true;
        clearTimeout(timer);
        unlinkAbortSignal();
    };
    return {
        signal: controller.signal,
        dispose
    };
};

const bindResponseLifetime = (response: Response, dispose: () => void): Response => {
    if (!response.body) {
        dispose();
        return response;
    }

    const reader = response.body.getReader();
    const body = new ReadableStream<Uint8Array>({
        pull: async (controller) => {
            try {
                const result = await reader.read();
                if (result.done) {
                    dispose();
                    controller.close();
                    return;
                }
                controller.enqueue(result.value);
            } catch (error: unknown) {
                dispose();
                controller.error(error);
            }
        },
        cancel: async (reason: unknown) => {
            try {
                await reader.cancel(reason);
            } finally {
                dispose();
            }
        }
    });
    return new Response(body, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers
    });
};

const encodeQuerySpaces = async (input: Request): Promise<Request> => {
    // URLSearchParams writes a space as '+', but ASP.NET Core query binding only decodes '%20'. A literal '+' is always sent as
    // '%2B', so every '+' left in the query string is a space and can be rewritten without changing what the server receives.
    const url = new URL(input.url);
    if (!url.search.includes("+")) {
        return input;
    }
    url.search = url.search.replaceAll("+", "%20");
    // Copy the request field by field: some Request implementations (jsdom's, in Vitest) ignore a Request passed as the init,
    // which sent a POST as a GET with no body. Reading the body (small JSON or form data) avoids streaming it into the copy.
    const hasBody = input.method !== "GET" && input.method !== "HEAD";
    return new Request(url.toString(), {
        method: input.method,
        headers: input.headers,
        body: hasBody ? await input.arrayBuffer() : undefined,
        signal: input.signal,
        credentials: input.credentials,
        redirect: input.redirect
    });
};

const captureResponseHeaders = (response: Response, options: ClientOptions<ClientContext>) => {
    for (const key of responseHeaderKeys) {
        const value = response.headers.get(key);
        if (value) {
            options.context[key] = value;
        }
    }
};

export const fetchFunction = async (input: Request, init: RequestInit, options: ClientOptions<ClientContext>, path: readonly string[]) => {
    const requestSignal = init.signal ?? input.signal;
    const timeout = getTimeout(path, options.context.timeoutInMilliseconds);

    const requestController = createRequestController(requestSignal, timeout);

    try {
        input = await encodeQuerySpaces(input);

        const res = await fetch(input, { ...init, signal: requestController.signal });
        captureResponseHeaders(res, options);

        // 204 No Content has an empty body — oRPC fails to parse it and throws a parse error.
        // Convert to 200 + JSON null so the .nullable() Zod schema can accept it cleanly.
        if (res.status === 204) {
            return bindResponseLifetime(
                new Response("null", {
                    status: 200,
                    headers: { "content-type": "application/json; charset=utf-8" }
                }),
                requestController.dispose
            );
        }

        const contentType = res.headers.get("content-type");
        if (contentType?.includes("problem+json")) {
            const body = await res.text();
            const newHeaders = new Headers(res.headers);
            newHeaders.set("content-type", "application/json; charset=utf-8");

            return bindResponseLifetime(
                new Response(body, {
                    status: res.status,
                    statusText: res.statusText,
                    headers: newHeaders
                }),
                requestController.dispose
            );
        }

        if (!res.ok && contentType?.includes("json")) {
            const body = await res.text();
            return bindResponseLifetime(
                new Response(isParsableJson(body) ? body : JSON.stringify(body), {
                    status: res.status,
                    statusText: res.statusText,
                    headers: res.headers
                }),
                requestController.dispose
            );
        }

        const isJsonBody = !contentType || contentType.includes("json");
        if (isJsonBody && res.body && !res.headers.has("content-disposition")) {
            const body = await res.arrayBuffer();
            requestController.dispose();
            return new Response(body, {
                status: res.status,
                statusText: res.statusText,
                headers: res.headers
            });
        }

        return bindResponseLifetime(res, requestController.dispose);
    } catch (error: unknown) {
        requestController.dispose();
        throw error;
    }
};
